import React, { useState, useEffect } from 'react';
import { getScheduleByIMO } from '../services/VesselApiService';
import { fetchSAECSSchedules } from '../services/maerskApi';
import { fetchAmexSchedules } from '../services/AmexApi';
import { fetchMesawaSchedules } from '../services/MesawaApi';
import { ArrowLeft, Calendar, Anchor, Ship, MapPin } from 'lucide-react';
import LoadingIndicator from './LoadingIndicator';

const VesselScheduleView = ({ vessel, onBack }) => {
  const [scheduleData, setScheduleData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedSchedule, setParsedSchedule] = useState([]);
  const [serviceData, setServiceData] = useState(null);
  const [serviceType, setServiceType] = useState(null);

  useEffect(() => {
    const fetchScheduleData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Try using the IMO number to fetch the vessel schedule
        const data = await getScheduleByIMO(vessel.imo || vessel.vesselIMONumber);
        console.log('Raw vessel schedule data from IMO lookup:', data);
        setScheduleData(data);

        // Parse the schedule data
        if (data && data.vesselSchedules && data.vesselSchedules.length > 0) {
          const firstSchedule = data.vesselSchedules[0];
          
          if (firstSchedule.portCalls && Array.isArray(firstSchedule.portCalls)) {
            // Extract port calls and sort by date
            const portCalls = firstSchedule.portCalls.map(call => {
              // Extract and format various schedule elements
              const arrival = call.transportEvents.find(e => e.transportEventTypeCode === "ARRI");
              const departure = call.transportEvents.find(e => e.transportEventTypeCode === "DEPA");
              
              return {
                port: {
                  name: call.location.name || 'Unknown Port',
                  code: call.location.UNLocationCode || ''
                },
                arrival: {
                  timestamp: arrival ? arrival.actualDateTime || arrival.estimatedDateTime : null,
                  isActual: arrival && arrival.actualDateTime ? true : false
                },
                departure: {
                  timestamp: departure ? departure.actualDateTime || departure.estimatedDateTime : null,
                  isActual: departure && departure.actualDateTime ? true : false
                },
                facility: {
                  name: call.location.facilityName || 'N/A'
                },
                voyage: firstSchedule.voyage || null
              };
            });
            
            // Sort port calls by arrival time
            portCalls.sort((a, b) => {
              const timeA = a.arrival.timestamp ? new Date(a.arrival.timestamp).getTime() : 0;
              const timeB = b.arrival.timestamp ? new Date(b.arrival.timestamp).getTime() : 0;
              return timeA - timeB;
            });
            
            setParsedSchedule(portCalls);
          }
        } else {
          // No data from IMO lookup, try service-specific schedules
          console.log('No data from IMO lookup, checking service schedules...');
          
          const vesselName = vessel.name || vessel.vesselName;
          const serviceChecks = [
            { name: 'SAECS', fetchFn: fetchSAECSSchedules },
            { name: 'AMEX', fetchFn: fetchAmexSchedules },
            { name: 'MESAWA', fetchFn: fetchMesawaSchedules }
          ];
          
          let foundInService = false;
          
          // Try each service until we find the vessel
          for (const service of serviceChecks) {
            if (foundInService) break;
            
            try {
              console.log(`Checking ${service.name} service...`);
              const scheduleData = await service.fetchFn();
              
              if (!scheduleData) continue;
              
              // Check if vessel is in current rotation
              const foundInCurrent = scheduleData.current?.vessels?.find(v => 
                (v.name === vesselName || v.vesselName === vesselName) ||
                // Also try to match by IMO number if available
                (vessel.imo && (v.vesselIMONumber === vessel.imo || v.imo === vessel.imo))
              );
              
              // Check if vessel is in upcoming rotation
              const foundInUpcoming = scheduleData.upcoming?.vessels?.find(v => 
                (v.name === vesselName || v.vesselName === vesselName) ||
                // Also try to match by IMO number if available
                (vessel.imo && (v.vesselIMONumber === vessel.imo || v.imo === vessel.imo))
              );
              
              const foundVessel = foundInCurrent || foundInUpcoming;
              console.log(`Found vessel in ${service.name} schedules:`, foundVessel);
              
              if (foundVessel) {
                setServiceData(scheduleData);
                setServiceType(service.name);
                foundInService = true;
                
                // Extract port calls from the service
                const extractedCalls = extractPortCallsFromService(
                  scheduleData, 
                  vesselName, 
                  service.name, 
                  foundInCurrent ? 'current' : 'upcoming'
                );
                
                setParsedSchedule(extractedCalls);
              }
            } catch (error) {
              console.error(`Error checking ${service.name} schedule:`, error);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching vessel schedule:', err);
        setError(`Failed to load vessel schedule: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Helper function to extract port calls from service data
    const extractPortCallsFromService = (serviceSchedules, vesselName, serviceName, rotation = 'current') => {
      const extractedCalls = [];
      
      // Process load ports
      const loadPorts = serviceSchedules[rotation].loadPorts;
      
      // Find vessel index in the rotation
      const vesselIndex = serviceSchedules[rotation].vessels.findIndex(v => 
        (v.name === vesselName || v.vesselName === vesselName) ||
        (vessel.imo && (v.vesselIMONumber === vessel.imo || v.imo === vessel.imo))
      );
      
      if (vesselIndex >= 0) {
        loadPorts.forEach(port => {
          if (port.schedules && port.schedules[vesselIndex]) {
            const schedule = port.schedules[vesselIndex];
            
            // Skip ports with no schedule data
            if (schedule.eta?.text === 'N/A' && schedule.etd?.text === 'N/A') return;
            
            extractedCalls.push({
              port: {
                name: port.name,
                code: port.code
              },
              arrival: {
                timestamp: schedule.eta?.rawDateTime,
                isActual: schedule.eta?.isActual || schedule.eta?.type === 'ACT',
                displayText: schedule.eta?.text
              },
              departure: {
                timestamp: schedule.etd?.rawDateTime,
                isActual: schedule.etd?.isActual || schedule.etd?.type === 'ACT',
                displayText: schedule.etd?.text
              },
              facility: {
                name: `${serviceName} Load Port`
              }
            });
          }
        });
        
        // Process discharge ports
        const dischargePorts = serviceSchedules[rotation].dischargePorts;
        
        dischargePorts.forEach(port => {
          if (port.schedules && port.schedules[vesselIndex]) {
            const schedule = port.schedules[vesselIndex];
            
            // Skip ports with no schedule data
            if (schedule.eta?.text === 'N/A' && schedule.etd?.text === 'N/A') return;
            
            extractedCalls.push({
              port: {
                name: port.name,
                code: port.code
              },
              arrival: {
                timestamp: schedule.eta?.rawDateTime,
                isActual: schedule.eta?.isActual || schedule.eta?.type === 'ACT',
                displayText: schedule.eta?.text
              },
              departure: {
                timestamp: schedule.etd?.rawDateTime,
                isActual: schedule.etd?.isActual || schedule.etd?.type === 'ACT',
                displayText: schedule.etd?.text
              },
              facility: {
                name: `${serviceName} Discharge Port`
              }
            });
          }
        });
      }
      
      // Sort by date if possible
      extractedCalls.sort((a, b) => {
        const dateA = a.arrival.timestamp ? new Date(a.arrival.timestamp).getTime() : 
                    a.departure.timestamp ? new Date(a.departure.timestamp).getTime() : 0;
        const dateB = b.arrival.timestamp ? new Date(b.arrival.timestamp).getTime() : 
                    b.departure.timestamp ? new Date(b.departure.timestamp).getTime() : 0;
        return dateA - dateB;
      });
      
      return extractedCalls;
    };

    fetchScheduleData();
  }, [vessel.imo, vessel.vesselIMONumber, vessel.name, vessel.vesselName]);

  const formatDate = (dateStr, displayText) => {
    if (displayText) return displayText;
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 min-h-[300px]">
        <LoadingIndicator />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading vessel schedule...</p>
      </div>
    );
  }

  // Header with back button
  const renderHeader = () => (
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
        {vessel.name || vessel.vesselName} Schedule
        {serviceType && <span className="ml-2 text-sm text-blue-500 dark:text-blue-400">({serviceType} Service)</span>}
      </h2>
      <button
        onClick={onBack}
        className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to search
      </button>
    </div>
  );

  // Get voyage number from any available source
  const voyageNumber = vessel.voyageNumber || vessel.voyage || 
    (serviceData && serviceType && serviceData.current?.vessels?.find(v => 
      (v.name === (vessel.name || vessel.vesselName) || v.vesselName === (vessel.name || vessel.vesselName)) ||
      (vessel.imo && (v.vesselIMONumber === vessel.imo || v.imo === vessel.imo))
    )?.voyageNumber ||
    serviceData?.upcoming?.vessels?.find(v => 
      (v.name === (vessel.name || vessel.vesselName) || v.vesselName === (vessel.name || vessel.vesselName)) ||
      (vessel.imo && (v.vesselIMONumber === vessel.imo || v.imo === vessel.imo))
    )?.voyageNumber);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden p-6">
      {renderHeader()}

      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between space-y-2 md:space-y-0">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center">
            <Ship className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">Vessel:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{vessel.name || vessel.vesselName}</span>
          </div>
          <div className="flex items-center">
            <Anchor className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">IMO:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{vessel.imo || vessel.vesselIMONumber}</span>
          </div>
        </div>
        {voyageNumber && (
          <div className="flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">Voyage:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{voyageNumber}</span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Port
              </th>
              <th className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Arrival
              </th>
              <th className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Departure
              </th>
              <th className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Terminal
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
            {parsedSchedule.map((portCall, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {portCall.port.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {portCall.port.code}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm ${portCall.arrival.isActual ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-900 dark:text-white'}`}>
                    {formatDate(portCall.arrival.timestamp, portCall.arrival.displayText)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {!portCall.arrival.displayText && formatTime(portCall.arrival.timestamp)}
                    {portCall.arrival.isActual && ' (Actual)'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm ${portCall.departure.isActual ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-900 dark:text-white'}`}>
                    {formatDate(portCall.departure.timestamp, portCall.departure.displayText)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {!portCall.departure.displayText && formatTime(portCall.departure.timestamp)}
                    {portCall.departure.isActual && ' (Actual)'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {portCall.facility.name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {parsedSchedule.length === 0 && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
          <p className="text-yellow-700 dark:text-yellow-400">No schedule data found for this vessel in any service.</p>
        </div>
      )}
    </div>
  );
};

export default VesselScheduleView;
